/**
 * The typed fetch client. Every network call in the app goes through here.
 *
 * Design notes that are load-bearing, not style:
 *
 * - `credentials: 'include'` on every request. The session is an httpOnly
 *   cookie (CONTRACT §4); there is no token in JS and nothing in localStorage.
 * - The CSRF double-submit token is read from the non-httpOnly `medtrack_csrf`
 *   cookie and echoed as `X-CSRF-Token` on every mutating verb.
 * - Responses are `{ data }` / `{ error }` envelopes. This unwraps `data` so
 *   call sites read `const meds = await api.get<Medication[]>(...)`.
 * - A 401 dispatches `medtrack:unauthorized` on window and the router handles
 *   the redirect once, centrally. Feature code must not handle 401.
 * - NO AUTOMATIC RETRY ON MUTATIONS. See the comment on `request()`.
 */

import type { ApiErrorCode } from '@medtrack/shared';

export const CSRF_COOKIE_NAME = 'medtrack_csrf';
export const CSRF_HEADER_NAME = 'X-CSRF-Token';
export const UNAUTHORIZED_EVENT = 'medtrack:unauthorized';

const API_BASE = '/api';

/** Verbs that change state and therefore carry the CSRF header. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Codes the client can produce on its own, on top of the server's set. The
 * server never emits these; they exist so `catch (e)` has one shape to check
 * instead of "ApiClientError or TypeError from fetch".
 */
export type ApiClientErrorCode = ApiErrorCode | 'network_error' | 'malformed_response';

/** Field-level validation detail. Keys are dotted paths, e.g. `recurrence.weekdays`. */
export type FieldErrors = Record<string, string>;

export class ApiClientError extends Error {
  readonly code: ApiClientErrorCode;
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  readonly fields?: FieldErrors;
  /** The request that failed, for logging. Never contains the request body. */
  readonly method: HttpMethod;
  readonly path: string;

  constructor(init: {
    code: ApiClientErrorCode;
    status: number;
    message: string;
    fields?: FieldErrors;
    method: HttpMethod;
    path: string;
  }) {
    super(init.message);
    this.name = 'ApiClientError';
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields;
    this.method = init.method;
    this.path = init.path;
    // Keeps `instanceof` working when this file is transpiled down.
    Object.setPrototypeOf(this, ApiClientError.prototype);
  }

  /** True when the failure is a field-level validation problem worth rendering inline. */
  get isValidation(): boolean {
    return this.code === 'validation_failed' && this.fields !== undefined;
  }
}

export function isApiClientError(value: unknown): value is ApiClientError {
  return value instanceof ApiClientError;
}

/* ------------------------------------------------------------------ *
 * Cookie + query helpers
 * ------------------------------------------------------------------ */

/** Read a non-httpOnly cookie. Returns null when absent or in a non-DOM env. */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (!raw) return null;
  for (const part of raw.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    const value = part.slice(eq + 1).trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
}

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

/**
 * Build a query string, dropping null/undefined. Booleans are stringified
 * explicitly: the API parses `includePrn` as the literal strings 'true'/'false'
 * (see the note in shared/api.ts about `Boolean("false")`), so `false` must
 * survive as "false" and not be dropped.
 */
export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === null || item === undefined) continue;
        search.append(key, String(item));
      }
      continue;
    }
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/* ------------------------------------------------------------------ *
 * Error parsing
 * ------------------------------------------------------------------ */

interface RawErrorEnvelope {
  error?: { code?: unknown; message?: unknown; fields?: unknown };
}

const KNOWN_CODES = new Set<string>([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'validation_failed',
  'rate_limited',
  'internal',
]);

function codeForStatus(status: number): ApiClientErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'validation_failed';
    case 429:
      return 'rate_limited';
    default:
      return 'internal';
  }
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'Not found.';
    case 409:
      return 'That conflicts with something already saved.';
    case 422:
      return 'Some of those details are not valid.';
    case 429:
      return 'Too many attempts. Wait a moment and try again.';
    default:
      return status >= 500
        ? 'The server had a problem. Nothing was saved.'
        : 'Something went wrong.';
  }
}

function parseFields(value: unknown): FieldErrors | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const out: FieldErrors = {};
  for (const [key, message] of Object.entries(value as Record<string, unknown>)) {
    if (typeof message === 'string') out[key] = message;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/* ------------------------------------------------------------------ *
 * The request core
 * ------------------------------------------------------------------ */

export interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  /** Extra headers. Cannot override the CSRF header. */
  headers?: Record<string, string>;
}

interface InternalOptions extends RequestOptions {
  body?: unknown;
}

let unauthorizedDispatchedAt = 0;

function dispatchUnauthorized(path: string): void {
  if (typeof window === 'undefined') return;
  // Several parallel queries can 401 at once on a session expiry. Collapse them
  // so the router does not receive a burst of redirects.
  const now = Date.now();
  if (now - unauthorizedDispatchedAt < 500) return;
  unauthorizedDispatchedAt = now;
  window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { path } }));
}

/**
 * One request. There is deliberately no retry loop in here.
 *
 * WHY NO RETRY ON MUTATIONS: a retried `POST /api/doses/occurrence` is a
 * double-dose risk. The server upserts on (scheduleId, scheduledFor) so a
 * duplicate would usually collapse — but "usually" is not a safety argument,
 * and `POST /api/doses/adhoc` has no unique key at all, so a retried PRN log
 * genuinely creates a second dose record. Worse, a request that timed out may
 * have succeeded: the client cannot tell "never arrived" from "arrived, reply
 * lost", and retrying the second case writes the dose twice. Idempotency on the
 * server is the safety net, not the plan. Reads may retry (configured once, in
 * the QueryClient defaults); mutations never do, anywhere.
 */
/**
 * THIS CLIENT OWNS THE `/api` PREFIX. Call sites pass paths WITHOUT it:
 *
 *   api.get('/profiles/123/occurrences')     correct
 *   api.get('/api/profiles/123/occurrences') wrong, produces /api/api/...
 *
 * That mistake shipped once already. It is nasty because it fails as a 404 on
 * some screens while other screens work fine, which reads like a routing bug in
 * the API rather than a client one. So rather than trusting every call site to
 * remember, strip a redundant prefix here and shout about it in development.
 */
function normalisePath(path: string): string {
  const withSlash = path.startsWith('/') ? path : `/${path}`;

  if (withSlash === API_BASE || withSlash.startsWith(`${API_BASE}/`)) {
    if (import.meta.env.DEV) {
      // Loud on purpose: silently correcting it would let the wrong pattern
      // spread through the codebase unnoticed.
      console.error(
        `[api] Path "${path}" already starts with "${API_BASE}". The client adds ` +
          `that prefix itself. Stripping it, but fix the call site.`,
      );
    }
    return withSlash.slice(API_BASE.length) || '/';
  }

  return withSlash;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  options: InternalOptions = {},
): Promise<T> {
  const url = `${API_BASE}${normalisePath(path)}${buildQuery(options.params)}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };

  const hasBody = options.body !== undefined;
  if (hasBody) headers['Content-Type'] = 'application/json';

  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    // A missing token is not fatal here: GET /api/auth/session re-issues the
    // cookie when the browser dropped it, so letting the request through
    // produces a clean 403 the user can retry rather than a silent no-op.
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (cause) {
    // An aborted request is a cancellation, not a failure; let it propagate as
    // itself so TanStack Query does not show it as an error state.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiClientError({
      code: 'network_error',
      status: 0,
      message: 'Could not reach the server. Check your connection.',
      method,
      path,
    });
  }

  if (response.status === 401) {
    dispatchUnauthorized(path);
  }

  // 204 and any other empty body.
  if (response.status === 204 || response.status === 205) {
    if (response.ok) return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiClientError({
        code: response.ok ? 'malformed_response' : codeForStatus(response.status),
        status: response.status,
        message: response.ok
          ? 'The server sent a response this app could not read.'
          : fallbackMessage(response.status),
        method,
        path,
      });
    }
  }

  if (!response.ok) {
    const envelope = (parsed ?? {}) as RawErrorEnvelope;
    const rawCode = envelope.error?.code;
    const code: ApiClientErrorCode =
      typeof rawCode === 'string' && KNOWN_CODES.has(rawCode)
        ? (rawCode as ApiErrorCode)
        : codeForStatus(response.status);
    const rawMessage = envelope.error?.message;
    const message =
      typeof rawMessage === 'string' && rawMessage.trim().length > 0
        ? rawMessage
        : fallbackMessage(response.status);

    throw new ApiClientError({
      code,
      status: response.status,
      message,
      fields: parseFields(envelope.error?.fields),
      method,
      path,
    });
  }

  if (parsed === undefined) {
    // 200 with an empty body. Treat as void rather than inventing a value.
    return undefined as T;
  }

  if (typeof parsed !== 'object' || parsed === null || !('data' in parsed)) {
    throw new ApiClientError({
      code: 'malformed_response',
      status: response.status,
      message: 'The server sent a response this app could not read.',
      method,
      path,
    });
  }

  return (parsed as { data: T }).data;
}

/* ------------------------------------------------------------------ *
 * Public surface
 * ------------------------------------------------------------------ */

export const api = {
  get<T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, { ...options, params: params ?? options?.params });
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, { ...options, body });
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, { ...options, body });
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, { ...options, body });
  },
  async del(path: string, options?: RequestOptions): Promise<void> {
    await request<void>('DELETE', path, options);
  },
  /** Escape hatch for a DELETE that returns a body. Rare; 204 is the norm. */
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, options);
  },
} as const;

export type Api = typeof api;
export default api;
